import type { Candidate, ProbedSource, QualityTier } from '$types';
import { factsOf, isKnownDead, rankCandidates, type RankContext } from './rank';
import { sameLang } from './release';
import { preferredAudioTrack, trackLanguage } from '../player/track-selection';
import { sameTrackLanguage, normalizeTrackLanguage } from '../track-language';

export type SelectedQuality = Exclude<QualityTier, 'unknown'>;
export const AUTO_LIMIT = 12;
export const QUALITY_LEVELS: readonly SelectedQuality[] = ['2160p', '1080p', '720p', '480p'];
const HEIGHT: Record<QualityTier, number> = { '2160p': 2160, '1080p': 1080, '720p': 720, '480p': 480, unknown: 0 };
export const qualityLabel = (quality: QualityTier): string => quality === '2160p' ? '4K' : quality === 'unknown' ? 'Auto' : quality;

/** Measured file size wins over an aggregator's advertised bitrate or pack size. */
export function deliveryBitrate(c: Candidate, durationSec = 0): number {
  if ((c.check?.sizeBytes ?? 0) > 0 && durationSec > 0) return c.check!.sizeBytes! * 8 / durationSec;
  if (Number.isFinite(c.bitrateBps) && c.bitrateBps > 0) return c.bitrateBps;
  return c.sizeBytes > 0 && durationSec > 0 ? c.sizeBytes * 8 / durationSec : 0;
}

/** Width identifies letterboxed releases whose stored height omits the black bars. */
export function probedQuality(probe: ProbedSource): QualityTier {
  const video = probe.tracks.find(t => t.kind === 'video');
  const native = (probe as ProbedSource & { nativeDirect?: {widthPx:number; heightPx:number} }).nativeDirect;
  const width = video?.widthPx ?? native?.widthPx ?? 0;
  const height = video?.heightPx ?? native?.heightPx ?? 0;
  if (width >= 3600 || height >= 2000) return '2160p';
  if (width >= 1800 || height >= 1000) return '1080p';
  if (width >= 1200 || height >= 680) return '720p';
  if (width > 0 || height > 0) return '480p';
  return 'unknown';
}

export interface PreferredAudioAvailability {
  state: 'match' | 'mismatch' | 'unknown';
  audioIndex: number;
  reason: string;
}

/** Only verified track-local evidence counts. Release names/hints never do. */
export function preferredAudioAvailability(probe: ProbedSource | null | undefined, preferred: string): PreferredAudioAvailability {
  const label = audioLanguageLabel(preferred);
  if (!probe?.verified) return { state: 'unknown', audioIndex: -1, reason: `${label} audio has not been verified.` };
  const audioIndex = preferredAudioTrack(probe.tracks, preferred);
  if (audioIndex >= 0) return { state: 'match', audioIndex, reason: '' };
  const audio = probe.tracks.filter(t => t.kind === 'audio');
  // Native-direct probing may expose no audio metadata at all. Keep that
  // route usable only when the user explicitly permits the browser default.
  if (!preferred.trim() && audio.length === 0) return { state: 'match', audioIndex: -1, reason: '' };
  const unknown = audio.length === 0 || audio.some(t => !sameTrackLanguage(trackLanguage(t), trackLanguage(t)));
  return {
    state: unknown ? 'unknown' : 'mismatch', audioIndex: -1,
    reason: unknown ? `${label} audio has not been verified.` : `No ${label} audio track was found.`
  };
}

/** Describe the bounded search result without claiming the whole catalogue lacks a language. */
export function audioFallbackMessage(probe: ProbedSource, requested: SelectedQuality, preferred: string): string | null {
  const actual = probedQuality(probe);
  if (!preferred.trim() || actual === 'unknown' || HEIGHT[actual] >= HEIGHT[requested] ||
      preferredAudioAvailability(probe, preferred).state !== 'match') return null;
  const label = audioLanguageLabel(preferred);
  return `Using ${qualityLabel(actual)} with verified ${label} audio. No ${qualityLabel(requested)} copy with verified ${label} audio was found in the checked sources.`;
}

function audioLanguageLabel(preferred: string): string {
  if (!preferred.trim()) return 'Default';
  try { return new Intl.DisplayNames(['en'], { type: 'language' }).of(normalizeTrackLanguage(preferred)) ?? preferred; }
  catch { return preferred; }
}

export interface QualityAvailability {
  state: 'available' | 'checking' | 'unavailable';
  disabled: boolean;
  reason: string;
  candidates: Candidate[];
}
export type ProbeForCandidate = (candidate: Candidate) => ProbedSource | null | undefined;

/** Keep a bounded budget for every resolution, so 4K cannot consume the whole walk. */
export function tierProbeCandidates(candidates: readonly Candidate[], probeFor?: ProbeForCandidate): Candidate[] {
  const counts = new Map<QualityTier, number>();
  return candidates.filter(c => {
    const probe = probeFor?.(c);
    const quality = probe?.verified ? probedQuality(probe) : c.quality;
    const count = counts.get(quality) ?? 0;
    counts.set(quality, count + 1);
    return count < AUTO_LIMIT;
  });
}

/** undefined = unchecked/in flight; null = check failed. Neither proves audio. */
export function qualityAvailability(
  candidates: readonly Candidate[], quality: SelectedQuality, preferred: string, probeFor: ProbeForCandidate,
  options: { checking?: boolean } = {}
): QualityAvailability {
  const matching: Candidate[] = [];
  let unchecked = false;
  let unknownAudio = false;
  let wrongAudio = false;
  for (const c of candidates) {
    if (isKnownDead(c) || /\b(cam|camrip|hdcam|telesync|hdts|screener|dvdscr)\b/i.test(c.title.replace(/[._]/g, ' '))) continue;
    const probe = probeFor(c);
    const actual = probe?.verified ? probedQuality(probe) : c.quality;
    if (actual !== quality) continue;
    if (probe === undefined) { unchecked = true; continue; }
    if (!probe?.verified) continue;
    const audio = preferredAudioAvailability(probe, preferred);
    if (audio.state === 'match') matching.push(c);
    else if (audio.state === 'unknown') unknownAudio = true;
    else wrongAudio = true;
  }
  if (matching.length) return { state: 'available', disabled: false, reason: '', candidates: matching };
  const label = audioLanguageLabel(preferred);
  return {
    state: unchecked && options.checking ? 'checking' : 'unavailable', disabled: true, candidates: [],
    reason: unchecked && options.checking ? `Checking ${label} audio at ${qualityLabel(quality)}.`
      : unchecked || unknownAudio || wrongAudio ? `No verified ${label} audio at ${qualityLabel(quality)}.`
      : `No verified ${qualityLabel(quality)} copy is available.`
  };
}

/** Quality is a constraint, not a score bonus. Never cross an explicit tier. */
export function qualityCandidates(candidates: readonly Candidate[], ctx: RankContext, exact?: SelectedQuality, probeFor?: ProbeForCandidate): Candidate[] {
  const originals = new Map(candidates.map(c => [c.id, c]));
  const measured = candidates.filter(c => {
    const probe = probeFor?.(c);
    return probe === undefined || (probe !== null && preferredAudioAvailability(probe, ctx.audioLang ?? '').state === 'match');
  }).map(c => {
    const probe = probeFor?.(c);
    return probe?.verified ? { ...c, quality: probedQuality(probe) } : c;
  });
  const ranked = rankCandidates(measured, ctx);
  const eligible = ranked.filter(({ candidate: c }) =>
    !isKnownDead(c) && (exact ? c.quality === exact : HEIGHT[c.quality] <= HEIGHT[ctx.maxQuality]) &&
    !/\b(cam|camrip|hdcam|telesync|hdts|screener|dvdscr)\b/i.test(c.title.replace(/[._]/g, ' '))
  );
  // Within a tier, prefer usable routes, then credible encodes, then fewer bytes.
  // These floors are conservative ranking heuristics, not perceptual-quality proof.
  const floor: Record<QualityTier, number> = { '2160p': 6e6, '1080p': 2e6, '720p': 1e6, '480p': 0.4e6, unknown: 0 };
  function metrics(c: Candidate) {
    const facts = factsOf(c, ctx.caps);
    const need = deliveryBitrate(c, ctx.durationSec);
    const have = ctx.throughputFor?.(c) || (c.check?.popBytesPerSec ?? 0) * 8;
    const efficientCodec = facts.videoCodec === 'hevc' || facts.videoCodec === 'av1';
    const tooThin = need > 0 && need < floor[c.quality] * (efficientCodec ? 0.6 : 1);
    const size = c.check?.sizeBytes || c.sizeBytes;
    return {
      unsupported: facts.playability === 'unsupported-video' ? 1 : 0,
      starved: have > 0 && need > 0 && have < need * 1.5 ? 1 : 0,
      thin: tooThin ? 1 : 0,
      unverified: c.check?.state === 'fast' || c.check?.state === 'slow' ? 0 : 1,
      metered: facts.store === 'Premiumize' ? 1 : 0,
      language: ctx.audioLang && c.langHints.length && !c.langHints.some(l => l === 'multi' || sameLang(l, ctx.audioLang)) ? 1 : 0,
      // Never compare bytes with bits/second when duration is unknown.
      unknownCost: size > 0 || need > 0 ? 0 : 1,
      cost: size > 0 ? size : need > 0 && (ctx.durationSec ?? 0) > 0 ? need * ctx.durationSec! / 8 : Infinity
    };
  }
  const rows = eligible.map(r => ({ ...r, m: metrics(r.candidate) }));
  rows.sort((a, b) =>
    a.m.unsupported - b.m.unsupported || a.m.starved - b.m.starved ||
    HEIGHT[b.candidate.quality] - HEIGHT[a.candidate.quality] ||
    a.m.thin - b.m.thin || a.m.unverified - b.m.unverified ||
    a.m.metered - b.m.metered || a.m.language - b.m.language ||
    a.m.unknownCost - b.m.unknownCost || a.m.cost - b.m.cost || b.score - a.score
  );
  return rows.map(r => originals.get(r.candidate.id)!);
}
